// src/app/services/overlay.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PlanogramService } from './planogram.service';
import { LayoutService } from './layout.service';
import { ProductStatus } from '../models/layout/product-status';

export enum OverlayType {
  NONE = 'none',
  DOS = 'dos',
  CPI_CATEGORY = 'cpi_cat',
  CPI_SEGMENT = 'cpi_seg',
  SEGMENT = 'segment',
  SEGMENT_FAMILY = 'segment_family',
  RSP = 'rsp',
  VISUAL_ANALYSIS = 'visual_analysis',
  LIFECYCLE_STATUS = 'lifecycle_status',
  LIFECYCLE_RANGE = 'lifecycle_range',
  LIFECYCLE_PLAN = 'lifecycle_plan',
  CASE_PACK = 'case_pack',
}

@Injectable({
  providedIn: 'root'
})
export class OverlayService {
  private overlayTypeSubject = new BehaviorSubject<OverlayType>(OverlayType.NONE);
  private productLegendMapSubject = new BehaviorSubject<Map<number, number>>(new Map<number, number>());
  private rspBoundaries: {q1: number, q2: number, q3: number} | null = null;
  private highlightedOverlayIndexSubject = new BehaviorSubject<number | null>(null);

  overlayType$: Observable<OverlayType> = this.overlayTypeSubject.asObservable();
  productLegendMap$: Observable<Map<number, number>> = this.productLegendMapSubject.asObservable();
  highlightedOverlayIndex$: Observable<number | null> = this.highlightedOverlayIndexSubject.asObservable();

  overlayColors = [
    // Primary and secondary colors
    'red', 'blue', 'green', 'yellow', 'purple', 'orange',
    // Additional base colors
    'pink', 'cyan', 'brown', 'magenta', 'teal', 'indigo',
    // Light variations
    'lightblue', 'lightgreen', 'lightpink', 'lightsalmon', 'lightseagreen', 'lightskyblue',
    // Dark variations
    'darkred', 'darkblue', 'darkgreen', 'darkorange', 'darkcyan', 'darkmagenta',
    // Special hues
    'coral', 'crimson', 'deeppink', 'dodgerblue', 'forestgreen', 'fuchsia',
    'goldenrod', 'hotpink', 'indianred', 'limegreen', 'mediumorchid', 'mediumpurple',
    'mediumseagreen', 'mediumslateblue', 'mediumturquoise', 'mediumvioletred',
    // Additional variations
    'olivedrab', 'orangered', 'orchid', 'palevioletred', 'peru', 'plum',
    'rosybrown', 'royalblue', 'saddlebrown', 'salmon', 'sandybrown', 'seagreen',
    'sienna', 'slateblue', 'springgreen', 'steelblue', 'tomato', 'violet'
  ];
  productDOSMap: Map<number, number> = new Map<number, number>();
  productCapacityMap: Map<number, number> = new Map<number, number>();

  constructor(
    private planogramService: PlanogramService,
    private layoutService: LayoutService) {
    // Layout structure only affects SEGMENT/SEGMENT_FAMILY overlays (which scan
    // the layout to discover present segments). All other overlay types derive
    // their legend purely from planogramItems or layoutDOS. Refreshing on every
    // layout$ emission fans out an emission of productLegendMap$ to every
    // product component's combineLatest, even when overlay is NONE — that's
    // the dominant cost on pickup/drop/undo/redo, so gate it explicitly.
    this.layoutService.layout$.subscribe((layout) => {
      if (!layout) return;
      const type = this.overlayTypeSubject.value;
      if (type === OverlayType.SEGMENT || type === OverlayType.SEGMENT_FAMILY) {
        this.refreshLegendMap();
      }
    });
    this.layoutService.productsChanged$.subscribe(() => {
      if (this.overlayTypeSubject.value !== OverlayType.NONE) {
        this.refreshLegendMap();
      }
    });
    this.layoutService.layoutDOS$.subscribe((map) => {
      this.productDOSMap = map;
      if (this.overlayTypeSubject.value === OverlayType.DOS) {
        this.refreshLegendMap();
      }
    });
    this.layoutService.layoutCapacity$.subscribe((map) => {
      this.productCapacityMap = map;
      if (this.overlayTypeSubject.value === OverlayType.CASE_PACK) {
        this.refreshLegendMap();
      }
    });
  }

  getOverlayType() {
    return this.overlayTypeSubject.value;
  }

  clearOverlay(){
      this.overlayTypeSubject.next(OverlayType.NONE);
  }

  setOverlayType(overlayType: OverlayType) {
    if (this.overlayTypeSubject.value != overlayType) {
      this.updateLegendMap(overlayType);
      this.overlayTypeSubject.next(overlayType);
    }
  }

  refreshLegendMap() {
    this.updateLegendMap(this.overlayTypeSubject.value);
  }
  
  getRSPBoundaries() {
    return this.rspBoundaries;
  }

  setHighlightedOverlayIndex(index: number | null) {
    this.highlightedOverlayIndexSubject.next(index);
  }

  // Helper to get index of a specific color string
  getColorIndex(color: string): number {
    return this.overlayColors.indexOf(color);
  }

  private updateLegendMap(overlayType: OverlayType) {
    /*
    [
      { color: 'red', text: 'CPI <= 200' },
      { color: 'orange', text: 'CPI <= 400' },
      { color: 'yellow', text: 'CPI <= 600' },
      { color: 'green', text: 'CPI > 600' }
    ];
    */
    const newLegendMap = new Map<number, number>();

    let redIndex = this.overlayColors.indexOf('red');
    let orangeIndex = this.overlayColors.indexOf('orange');
    let yellowIndex = this.overlayColors.indexOf('yellow');
    let greenIndex = this.overlayColors.indexOf('green');
    let blueIndex = this.overlayColors.indexOf('blue');
    let cyanIndex = this.overlayColors.indexOf('cyan');
    let goldenrodIndex = this.overlayColors.indexOf('goldenrod');
    let steelblueIndex = this.overlayColors.indexOf('steelblue');
    let limegreenIndex = this.overlayColors.indexOf('limegreen');
    let orangeredIndex = this.overlayColors.indexOf('orangered');
    let saddlebrownIndex = this.overlayColors.indexOf('saddlebrown');

    if (overlayType == OverlayType.DOS) {
      let minDos = this.planogramService.getSetting('dos-min') || 7;
      let maxDos = this.planogramService.getSetting('dos-max') || 14;
      this.layoutService.planogramItems.forEach(item => {
        let totalDOS = this.productDOSMap.get(item.productId) ?? 0;
        if(totalDOS == 0 || !Number.isFinite(totalDOS) || Number.isNaN(totalDOS)){
          newLegendMap.set(item.productId, blueIndex) //blue
        }
        else if (totalDOS < minDos) {
          newLegendMap.set(item.productId, redIndex) //red
        } 
        else if (totalDOS > maxDos) {
          newLegendMap.set(item.productId, orangeIndex) // orange
        } 
        else {
          newLegendMap.set(item.productId, greenIndex) // green
        }
      });
    }
    else if (overlayType == OverlayType.CPI_CATEGORY) {
      const tier1 = this.planogramService.getSetting('cpi-tier1') || 200;
      const tier2 = this.planogramService.getSetting('cpi-tier2') || 400;
      const tier3 = this.planogramService.getSetting('cpi-tier3') || 600;
      this.layoutService.planogramItems.forEach(item => {
        if (item.cpi1 > tier3) {
          newLegendMap.set(item.productId, greenIndex)
        }
        else if (item.cpi1 <= tier3 && item.cpi1 > tier2) {
          newLegendMap.set(item.productId, yellowIndex)
        }
        else if (item.cpi1 <= tier2 && item.cpi1 > tier1) {
          newLegendMap.set(item.productId, orangeIndex)
        }
        else {
          newLegendMap.set(item.productId, redIndex)
        }
      });
    }
    else if (overlayType == OverlayType.CPI_SEGMENT) {
      const tier1 = this.planogramService.getSetting('cpi-tier1') || 200;
      const tier2 = this.planogramService.getSetting('cpi-tier2') || 400;
      const tier3 = this.planogramService.getSetting('cpi-tier3') || 600;
      
      this.layoutService.planogramItems.forEach(item => {
        if (item.cpi2 > tier3) {
          newLegendMap.set(item.productId, greenIndex)
        }
        else if (item.cpi2 <= tier3 && item.cpi2 > tier2) {
          newLegendMap.set(item.productId, yellowIndex)
        }
        else if (item.cpi2 <= tier2 && item.cpi2 > tier1) {
          newLegendMap.set(item.productId, orangeIndex)
        }
        else {
          newLegendMap.set(item.productId, redIndex)
        }
      });
    }
    else if (overlayType == OverlayType.SEGMENT) {
      let segments: string[] = [];

      let layout = this.layoutService.getCurrentLayout(false);

      if (layout) {
        layout?.drops.forEach(drop => {
          drop.shelves.forEach((shelf) => {
            shelf.stacks.forEach((stack) => {
              stack.products.forEach(product => {
                let reference = this.layoutService.planogramItems.get(product.productId)
                if (reference && segments.indexOf(reference.categoryLevel2) == -1)
                  segments.push(reference.categoryLevel2)
              });
            });
          });
        });

        this.layoutService.planogramItems.forEach(item => {
          newLegendMap.set(item.productId, segments.indexOf(item.categoryLevel2))
        });
      }
    }
    else if (overlayType == OverlayType.SEGMENT_FAMILY) {
      let segment_families: string[] = [];

      let layout = this.layoutService.getCurrentLayout(false);

      if (layout) {
        layout?.drops.forEach(drop => {
          drop.shelves.forEach((shelf) => {
            shelf.stacks.forEach((stack) => {
              stack.products.forEach((product) => {
                let reference = this.layoutService.planogramItems.get(product.productId);
                if(reference){
                  let segment_family = `${reference.categoryLevel2} - ${reference.categoryLevel3}`;
                  if (segment_families.indexOf(segment_family) == -1)
                    segment_families.push(segment_family)
                }
              });
            })
          });
        });

        this.layoutService.planogramItems.forEach(item => {
          let segment_family = `${item.categoryLevel2} - ${item.categoryLevel3}`;
          newLegendMap.set(item.productId, segment_families.indexOf(segment_family))
        });
      }
    }
    else if (overlayType == OverlayType.RSP) {
      let prices: number[] = [];
      
      // Collect all prices
      this.layoutService.planogramItems.forEach(item => {
        if(item.salesData.averagePrice > 0)
          prices.push(item.salesData.averagePrice);
      });
  
      // Calculate statistics
      const mean = prices.reduce((a, b) => a + b, 0) / prices.length;
      const standardDev = Math.sqrt(
        prices.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / prices.length
      );
  
      // Calculate boundaries using standard deviations
      const q1 = mean - standardDev; // -1 SD
      const q2 = mean;               // mean
      const q3 = mean + standardDev; // +1 SD
  
      // Store boundaries for legend component to use
      this.rspBoundaries = {q1, q2, q3};
  
      // Assign colors based on statistical boundaries
      this.layoutService.planogramItems.forEach(item => {
        const rsp = item.salesData.averagePrice;
        if(rsp == 0){
          newLegendMap.set(item.productId, blueIndex);
        }
        else if (rsp < q1) {
          newLegendMap.set(item.productId, greenIndex);
        }
        else if (rsp >= q1 && rsp < q2) {
          newLegendMap.set(item.productId, yellowIndex);
        }
        else if (rsp >= q2 && rsp < q3) {
          newLegendMap.set(item.productId, orangeIndex);
        }
        else {
          newLegendMap.set(item.productId, redIndex);
        }
      });
    }
    else if (overlayType == OverlayType.LIFECYCLE_STATUS) {
      this.layoutService.planogramItems.forEach(item => {
        const statusId = item.statusId ?? ProductStatus.Active;
        if (statusId === ProductStatus.NPI) {
          newLegendMap.set(item.productId, cyanIndex);
        } else if (statusId === ProductStatus.Discontinued) {
          newLegendMap.set(item.productId, saddlebrownIndex);
        } else if (statusId === ProductStatus.ToBeDiscontinued) {
          newLegendMap.set(item.productId, goldenrodIndex);
        } else {
          newLegendMap.set(item.productId, greenIndex);
        }
      });
    }
    else if (overlayType == OverlayType.LIFECYCLE_RANGE) {
      this.layoutService.planogramItems.forEach(item => {
        if (item.ranged) {
          newLegendMap.set(item.productId, greenIndex);
        } else {
          newLegendMap.set(item.productId, steelblueIndex);
        }
      });
    }
    else if (overlayType == OverlayType.LIFECYCLE_PLAN) {
      this.layoutService.planogramItems.forEach(item => {
        if (item.willAdd) {
          newLegendMap.set(item.productId, limegreenIndex);
        } else if (item.willRemove) {
          newLegendMap.set(item.productId, orangeredIndex);
        }
      });
    }
    else if (overlayType == OverlayType.CASE_PACK) {
      // Compare each product's on-shelf capacity (units the placed facings hold)
      // against its case pack. Products with no case pack, or not currently
      // placed, are left uncoloured — the two legends only make sense when a
      // real capacity-vs-casepack comparison exists.
      this.layoutService.planogramItems.forEach(item => {
        const casePack = item.casePack;
        if (casePack == null || casePack <= 0) return;
        const capacity = this.productCapacityMap.get(item.productId) ?? 0;
        if (capacity <= 0) return;
        if (capacity < casePack) {
          newLegendMap.set(item.productId, redIndex);     // Under
        } else {
          newLegendMap.set(item.productId, greenIndex);  // Over
        }
      });
    }
    this.productLegendMapSubject.next(newLegendMap);
  }

  getCPIThresholds() {
    return {
      tier1: this.planogramService.getSetting('cpi-tier1') || 200,
      tier2: this.planogramService.getSetting('cpi-tier2') || 400,
      tier3: this.planogramService.getSetting('cpi-tier3') || 600
    };
  }
}